import React, { useState, useEffect } from "react";
import "./Registration.css";
import googleLogo from "../images/logo-google-svgrepo-com.svg";
import logo from "../images/download.svg";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import image1 from "../images/dark-green-pickleball-court.jpg";
import image2 from "../images/padel-court.jpg";

const slideshowImages = [image1, image2];

const Registration = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [titlePrefix, setTitlePrefix] = useState("Pickle");
  const [scrollDirection, setScrollDirection] = useState("down");

  const validateUsername = (username) => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      return "Username must be 3-20 characters long and contain only letters, numbers, and underscores.";
    }
    return null;
  };

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        console.error("Registration.js: Error checking session:", error);
        setError("Failed to check session. Please try again.");
        return;
      }
      if (session) {
        // //console.log('Registration.js: User already logged in:', session.user);
        navigate("/");
      }
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // //console.log('Registration.js: Auth state changed:', event, session);
        if (event === "SIGNED_IN" && session) {
          // //console.log('Registration.js: User signed in:', session.user);
          // //console.log('Registration.js: User metadata:', session.user.user_metadata);
          navigate("/");
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    let isFirstRun = true;

    const interval = setInterval(() => {
      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      setCurrentSlide((prevSlide) => (prevSlide + 1) % slideshowImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slideshowImages.length]);

  useEffect(() => {
    const newTitlePrefix = currentSlide === 0 ? "Pickle" : "Padel";
    const newDirection = currentSlide === 0 ? "down" : "up";
    setTitlePrefix(newTitlePrefix);
    setScrollDirection(newDirection);
  }, [currentSlide]);

  const handleGoogleRegister = async () => {
    try {
      setError(null);
      // //console.log('Registration.js: Initiating Google Sign-In');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        console.error(
          "Registration.js: Error during Google registration:",
          error
        );
        setError(`Google Registration Failed: ${error.message}`);
      } else {
        // //console.log('Registration.js: Google Sign-In initiated:', data);
      }
    } catch (error) {
      console.error(
        "Registration.js: Unexpected error during Google registration:",
        error
      );
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const usernameError = validateUsername(username);
    if (usernameError) {
      setError(usernameError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    try {
      // //console.log('Registration.js: Attempting to sign up with email:', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username },
          emailRedirectTo: `${window.location.origin}/login?registered=true`,
        },
      });

      if (error) {
        console.error(
          "Registration.js: Error during email registration:",
          error
        );
        setError(`Registration Failed: ${error.message}`);
        return;
      }

      // //console.log('Registration.js: Signup response:', data);
      if (data.user) {
        // //console.log('Registration.js: User created in auth.users:', data.user.id);
        setSuccess(
          "Registration successful! A verification email has been sent to your inbox. " +
            "Please check your email (including spam/junk folders) to verify your account, then click the link below to log in. " +
            "If you don’t receive the email within a few minutes, ensure your email provider isn’t blocking emails from Supabase."
        );
      } else {
        setError(
          "Registration succeeded, but no user data returned. Please try registering again."
        );
      }
    } catch (error) {
      console.error(
        "Registration.js: Unexpected error during email registration:",
        error
      );
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogoClick = () => {
    // //console.log("Registration.js: handleLogoClick called");
    const defaultState = {
      selectedBookingType: "Bookings",
      selectedProfile: false,
      selectedSport: null,
      selectedDate: null,
      selectedCourtType: null,
      selectedCourt: null,
      selectedTimeslots: [],
    };
    // //console.log("Registration.js: Navigating to / with state:", defaultState);
    navigate("/", { state: defaultState });
  };

  return (
    <div className="registration-page-container registration-page">
      <div className="registration-left-section">
        <div className="slideshow">
          {slideshowImages.map((image, index) => (
            <div
              key={index}
              className={`slide ${index === currentSlide ? "active" : ""}`}
              style={{
                backgroundImage: `url(${
                  typeof image === "string" ? image : image.default
                })`,
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="registration-right-section">
        <div
          className="logo-container"
          onClick={handleLogoClick}
          style={{ cursor: "pointer" }}
        >
          <img
            src={logo}
            alt="PickleDepot/PadelDepot Logo"
            className="registration-logo"
          />
        </div>
        <div className="title-wrapper">
          <div className="title-container">
            <h2 className="registration-title">
              <span className="title-prefix-wrapper">
                <span className={`title-prefix ${scrollDirection}`}>
                  <span className="title-prefix-item">Pickle</span>
                  <span className="title-prefix-item">Padel</span>
                  <span className="title-prefix-item">Pickle</span>
                </span>
              </span>
              <span className="title-suffix">Depot</span>
            </h2>
          </div>
        </div>
        {error && <p className="registration-error">{error}</p>}
        {success && (
          <div>
            <p className="registration-success">{success}</p>
            <p className="registration-login-link">
              <a href="/login?registered=true" className="registration-link">
                Proceed to Login
              </a>
            </p>
          </div>
        )}
        {!success && (
          <form className="registration-form" onSubmit={handleSubmit}>
            <div className="registration-form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                className="registration-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Enter your username"
              />
            </div>

            <div className="registration-form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className="registration-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="registration-form-group">
              <label htmlFor="password">Password</label>
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="registration-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                />
                <span
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <div className="registration-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  className="registration-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                />
                <span
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <button
              type="submit"
              className="registration-button"
              aria-label="Register with email and password"
            >
              Register
            </button>

            <p className="registration-divider">Or</p>

            <button
              type="button"
              className="registration-button registration-google-button"
              onClick={handleGoogleRegister}
              aria-label="Register with Google"
            >
              <div className="google-button-content">
                <img src={googleLogo} alt="Google Logo" />
                <span>Register with Google</span>
              </div>
            </button>
          </form>
        )}

        {!success && (
          <p className="registration-login-link">
            Already have an account?{" "}
            <a href="/login" className="registration-link">
              Login
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default Registration;
